import { useMemo } from 'react';
import useSWR, { KeyedMutator } from 'swr';
import {
    customSWRConfig,
    customSWRResponse,
    FieldDefinitionMap,
    IFieldDefinition,
    PaginatedData
} from '../model/types';
import { localizedSearchTermForApi } from '../utils/helpers';
import { refactorFieldOptions } from './ticketDetailPage/useFieldDefinitionListFromAgentForm';
import useSWRCacheKeys from './useSWRCacheKeys';

export default function usePaginatedOrganizationFieldDefinitionList<
    T extends { list: PaginatedData<IFieldDefinition>; listMap: FieldDefinitionMap }
>(
    params?: { size?: number; page?: number; isActive?: boolean; sort?: string; searchTerm?: string },
    config: customSWRConfig = {}
): customSWRResponse<T> {
    const keys = useSWRCacheKeys(
        `/organization-fields${
            params
                ? `?${params?.isActive ? 'active' : 'inactive'}${params?.size ? `&size=${params?.size}` : ''}${
                      params?.page ? `&page=${params?.page}` : ''
                  }${params?.sort ? `&sort=${params?.sort}` : ''}${
                      params?.searchTerm ? `&searchTerm=${localizedSearchTermForApi(params?.searchTerm)}` : ''
                  }`
                : ''
        }`
    );

    const { data, isLoading, mutate } = useSWR<PaginatedData<IFieldDefinition>>(keys, { ...config });

    const fields = useMemo(
        () =>
            data
                ? {
                      list: data,
                      listMap: data?.content?.reduce(
                          (acc, fd) => ({ ...acc, [fd.key]: refactorFieldOptions(fd) }),
                          {} as FieldDefinitionMap
                      )
                  }
                : ({ list: data?.content, listMap: data?.content } as unknown as T),
        [data]
    );

    return {
        data: fields as T,
        isLoading,
        mutate: mutate as unknown as KeyedMutator<T>
    };
}
function usePaginatedOrganizationFieldDefinitionListNoQuaryParams<
    T extends { list: PaginatedData<IFieldDefinition>; listMap: FieldDefinitionMap }
>(config: customSWRConfig = {}): customSWRResponse<T> {
    const keys = useSWRCacheKeys(`/organization-fields`);

    const { data, isLoading, mutate } = useSWR<PaginatedData<IFieldDefinition>>(keys, { ...config });

    const fields = useMemo(
        () =>
            data
                ? {
                      list: data?.content?.map((fd) => refactorFieldOptions(fd)),
                      listMap: data?.content?.reduce(
                          (acc, fd) => ({ ...acc, [fd.key]: refactorFieldOptions(fd) }),
                          {} as FieldDefinitionMap
                      )
                  }
                : ({ list: data?.content, listMap: data?.content } as unknown as T),
        [data]
    );

    return {
        data: fields as T,
        isLoading,
        mutate: mutate as unknown as KeyedMutator<T>
    };
}
function usePaginatedOrganizationFieldDefinitionListOnlyStatusParam<
    T extends { list: PaginatedData<IFieldDefinition>; listMap: FieldDefinitionMap }
>(isActive: boolean, config: customSWRConfig = {}): customSWRResponse<T> {
    const keys = useSWRCacheKeys(`/organization-fields?${isActive ? 'active' : 'inactive'}`);

    const { data, isLoading, mutate } = useSWR<PaginatedData<IFieldDefinition>>(keys, { ...config });

    const fields = useMemo(
        () =>
            data
                ? {
                      list: data,
                      listMap: data?.content?.reduce(
                          (acc, fd) => ({ ...acc, [fd.key]: refactorFieldOptions(fd) }),
                          {} as FieldDefinitionMap
                      )
                  }
                : ({ list: data?.content, listMap: data?.content } as unknown as T),
        [data]
    );

    return {
        data: fields as T,
        isLoading,
        mutate: mutate as unknown as KeyedMutator<T>
    };
}

export {
    usePaginatedOrganizationFieldDefinitionListNoQuaryParams,
    usePaginatedOrganizationFieldDefinitionListOnlyStatusParam
};
